import SettingsSummaryCard, { SettingDetail } from '../settings-summary-card/SettingsSummaryCard';
import { useTranslation } from 'react-i18next';
import { ReactElement, useMemo } from 'react';
import {
  LocaleModel,
  useGetLocaleFormatsQuery,
  useGetUiSettingsQuery,
} from '@casops/store/apis/settingsApi';
import { SettingsSidePanelTab } from '@casops/store/slices/globalSlice';
import { allTimezones, ITimezoneOption, useTimezoneSelect } from 'react-timezone-select';

export const LocalizationSettings = (): ReactElement => {
  const { t } = useTranslation();

  const { currentData: uiSettings } = useGetUiSettingsQuery();
  const { currentData: localeFormats } = useGetLocaleFormatsQuery();

  const { options: timezones } = useTimezoneSelect({
    labelStyle: 'original',
    displayValue: 'UTC',
    ...allTimezones,
  });

  const settings = useMemo<SettingDetail[]>(() => {
    let units: string | undefined;
    let dateTimeNumberFormat: string | undefined;
    let timezone: string | undefined;

    if (uiSettings) {
      // Translate the measurement unit.
      units = t(`MeasurementUnit.${uiSettings.localization.units}`);

      // Convert the dateTimeNumberFormat ID to a localeFormat name.
      if (localeFormats) {
        const matchingFormat = localeFormats.find(
          ({ id }: LocaleModel) => id === uiSettings.localization.dateTimeNumberFormat,
        );

        if (typeof matchingFormat !== 'undefined') {
          dateTimeNumberFormat = matchingFormat.name;
        }
      }

      // Convert the timezone value to a label.
      const matchingTimezone = timezones.find(({ value }: ITimezoneOption) => {
        return value === uiSettings.localization.timezone;
      });

      if (typeof matchingTimezone !== 'undefined') {
        timezone = matchingTimezone.label;
      }
    }

    return [
      [t('Fields.DateTimeNumberFormat'), dateTimeNumberFormat],
      [t('Fields.MeasurementUnit'), units],
      [t('Fields.Timezone'), timezone],
    ];
  }, [t, uiSettings, localeFormats, timezones]);

  return (
    <SettingsSummaryCard
      title={t('Navigation.Localization')}
      settings={settings}
      sidePanelTab={SettingsSidePanelTab.Localization}
    />
  );
};

export default LocalizationSettings;
